import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Link,
  useTranslate,
  ReactAdminComponentProps,
  TopToolbar,
  ListButton,
  TitleForRecord,
  Button,
  useRefresh,
  Loading,
  Identifier,
  useNotify,
  Confirm,
  useLocale,
  Responsive,
} from "react-admin";
import {
  Breadcrumbs,
  Typography,
  makeStyles,
  Paper,
  Divider,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  Card,
  Box,
  Menu,
  MenuItem,
  Button as MuiButton,
} from "@material-ui/core";
import resourceNames from "../../../../common/data/resourceNames";
import { RouteComponentProps, useHistory, useParams } from "react-router-dom";
import { CustomResourceRouteParams } from "../../../../common/types";
import classnames from "classnames";
import { useDriverLog } from "../../../../common/hooks/useDriverLog";
import { format, parseISO } from "date-fns";
import { DriverLogViewPager } from "./DriverLogViewPager";
import "./DriverLogView.css";
import { DriverLogHeader } from "./DriverLogHeader";
import { HosGraphView } from "./HosGraphView";
import GetAppIcon from "@material-ui/icons/GetApp";
import { HosDriverLogGridView } from "./HosDriverLogGridView";
import { useDriverLogViewParameters } from "../../../../common/hooks/useDriverLogViewParameters";
import { DriverLogViewFooter } from "./DriverLogViewFooter";
import { DriverSignature } from "./DriverSignature";
import { DriverLogInspectionReportsView } from "./DriverLogInspectionReportsView";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import {
  DriverApiFactory,
  DriverLogDetailedView,
  ReportApiApiFactory,
} from "../../../../common/data/service-proxies";
import { EventHoverContext } from "./HoverContext";
import { useSearchFields } from "../../../../common/hooks/useSearchFields";
import { ReassignEventsForm } from "./master-actions/ReassignEventsForm";
import { DriverLogViewChildRoutes } from "./DriverLogViewChildRoutes";
import { DriverLogExceptionsView } from "./DriverLogExceptionsView";
import { DriverNameBreadcrumb, SelectedDriver } from "./DriverNameBreadcrumb";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import {
  DateRange,
  ErrorOutline,
  ExitToApp,
  FindReplace,
  SwapHoriz,
  VisibilityOutlined,
  MoreHoriz,
} from "@material-ui/icons";
import { CycleRecapView } from "./CycleRecapView";
import { RolloverForm } from "./master-actions/RolloverForm";
import { ViewDriverPasswordAction } from "../../../administration/admin-drivers/ViewDriverPasswordAction";
import Icon from "@mdi/react";
import { mdiAccountSwitchOutline, mdiAutoFix } from "@mdi/js";
import { ShiftEventsForm } from "./master-actions/ShiftEventsForm";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import { useTitle } from "../../../../common/hooks/useTitle";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../../common/util/localization";
import { enUS } from "date-fns/locale";
import classNames from "classnames";
import { FixEventsForm } from "./master-actions/FixEventsForm";
import { IDriverLogGridView } from "./IDriverLogGridView";
import { useSignalR } from "../../../../context/SignalRContext";

interface DriverLogViewProps
  extends Partial<RouteComponentProps<CustomResourceRouteParams>> {}

interface ViewActionsProps
  extends Partial<RouteComponentProps<CustomResourceRouteParams>> {
  record: DriverLogDetailedView;
  loading: boolean;
  driverId?: number;
  logDate?: Date;
}

interface BreadcrumbProps {
  driver: SelectedDriver;
  logDate?: Date;
}

const useViewActionsStyles = makeStyles(
  (theme) => ({
    root: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(1),
      minHeight: theme.spacing(5),
      [theme.breakpoints.up("xs")]: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      [theme.breakpoints.down("sm")]: {
        paddingRight: theme.spacing(2),
      },
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
      },
    },
    warning: {
      color:
        theme.palette.type === "light"
          ? theme.palette.error.dark
          : theme.palette.error.light,
    },
    warningBox: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      padding: "4px 5px",
      "& svg": {
        fontSize: "20px",
      },
      "& p": {
        fontSize: "0.8125rem",
        textTransform: "uppercase",
        color:
          theme.palette.type === "light"
            ? theme.palette.error.dark
            : theme.palette.error.light,
      },
    },
  }),
  { name: "RaTopToolbar" }
);

const useStyles = makeStyles(
  {
    root: {},
    main: {
      display: "flex",
    },
    noActions: {
      marginTop: "1em",
    },
    card: {
      flex: "1 1 auto",
    },
  },
  { name: "RaShow" }
);

const ViewActions = ({
  record,
  driverId,
  logDate,
  loading,
  ...props
}: ViewActionsProps) => {
  const history = useHistory();
  const axios = useAxiosInstance();
  const [downloading, setDownloading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState<boolean>(false);
  const [fixEventsDialogOpen, setFixEventsDialogOpen] = useState<boolean>(
    false
  );
  const [recapOpen, setRecapOpen] = useState<boolean>(false);
  const [rolloverConfirmOpen, setRolloverConfirmOpen] = useState<boolean>(
    false
  );
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState<boolean>(false);
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = useMemo(
    () => (!!dateFnsLocaleMap[locale] ? dateFnsLocaleMap[locale] : enUS),
    [locale]
  );
  const title = useMemo(
    () =>
      `ProLogs - ${record.driverFullName} / ${format(
        parseISO(record.date!),
        "MMM do, yyyy",
        {
          locale: dateFnsLocale,
        }
      )}`,
    [dateFnsLocale, record.date, record.driverFullName]
  );
  useTitle(title);

  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();
  const hub = useSignalR();
  useSearchFields({ resource: resourceNames.ADMIN_DRIVERS }, ["driverName"]);
  const permissions = useActionPermissions();

  const handleSuccess = useCallback(() => {
    setDialogOpen(false);
    notify("resources.hos-driver-log-view.messages.reassignSuccess", {
      type: "success",
    });
    refresh();
  }, [refresh, notify]);

  const handleShiftSuccess = useCallback(() => {
    setShiftDialogOpen(false);
    notify("resources.hos-driver-log-view.messages.shiftSuccess", {
      type: "success",
    });
    refresh();
  }, [refresh, notify]);

  const handleRolloverSuccess = useCallback(
    (targetDriver: number) => {
      setRolloverConfirmOpen(false);
      notify("resources.hos-driver-log-view.messages.rolloverSuccess", {
        type: "success",
      });
      history.push(`/hos-driver-log-view/${targetDriver}/${record.startOfDay}`);
    },
    [history, notify, record]
  );

  const handleLogout = useCallback(() => {
    if (!axios) {
      return;
    }

    if (!record.driverId) {
      return;
    }

    DriverApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppDriverLogoutDriverIdPost(record.driverId)
      .then(() => {
        notify("Sent a message to log the driver out successfully", {
          type: "success",
        });
      })
      .catch((e) => {
        notify(handleAxiosError(e), { type: "error" });
      })
      .finally(() => {
        setLogoutConfirmOpen(false);
      });
  }, [axios, record.driverId, notify]);

  const handleFixEventsSuccess = useCallback(() => {
    setFixEventsDialogOpen(false);
    notify("resources.hos-driver-log-view.messages.fixEventsSuccess", {
      type: "success",
    });
    refresh();
  }, [refresh, notify]);

  const handleFixEventsFailed = useCallback(() => {
    setFixEventsDialogOpen(false);
    notify("resources.hos-driver-log-view.messages.fixEventsFailed", {
      type: "error",
    });
    refresh();
  }, [refresh, notify]);

  const handleFailed = useCallback(() => setDialogOpen(false), []);
  const handleShiftFailed = useCallback(() => setShiftDialogOpen(false), []);
  const handleRolloverFailed = useCallback(
    () => setRolloverConfirmOpen(false),
    []
  );

  const classes = useViewActionsStyles();

  const [
    masterMenuAnchorEl,
    setMasterMenuAnchorEl,
  ] = React.useState<null | HTMLElement>(null);
  const masterMenuOpen = Boolean(masterMenuAnchorEl);

  const handleMasterMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMasterMenuAnchorEl(event.currentTarget);
  };
  const handleMasterMenuClose = () => {
    setMasterMenuAnchorEl(null);
  };

  return (
    <>
      <TopToolbar className={`driver-log-top-toolbar ${classes.root}`}>
        <DriverLogViewPager
          record={record}
          loading={loading}
          history={history}
        />
        {permissions &&
        (permissions[permissionNames.hos.logs.rollover] ||
          permissions[permissionNames.hos.logs.reassign]) ? (
          <>
            <div className="master-actions">
              <Responsive
                small={
                  <>
                    <Button
                      label="Actions"
                      onClick={handleMasterMenuClick}
                      aria-controls={masterMenuOpen ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={masterMenuOpen ? "true" : undefined}
                    >
                      <MoreHoriz />
                    </Button>
                    <Menu
                      open={masterMenuOpen}
                      anchorEl={masterMenuAnchorEl}
                      onClose={handleMasterMenuClose}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                    >
                      {permissions &&
                      (permissions[permissionNames.hos.logs.rollover] ||
                        permissions[permissionNames.hos.logs.reassign]) ? (
                        <MenuItem>
                          <MuiButton
                            color="primary"
                            variant="text"
                            onClick={(e) => {
                              setShiftDialogOpen(true);
                              setMasterMenuAnchorEl(null);
                              e.stopPropagation();
                            }}
                            startIcon={<SwapHoriz />}
                          >
                            Shift Events
                          </MuiButton>
                        </MenuItem>
                      ) : null}
                      {permissions &&
                      permissions[permissionNames.hos.logs.rollover] ? (
                        <MenuItem>
                          <MuiButton
                            color="primary"
                            variant="text"
                            onClick={(e) => {
                              setRolloverConfirmOpen(true);
                              setMasterMenuAnchorEl(null);
                              e.stopPropagation();
                            }}
                            startIcon={<FindReplace />}
                          >
                            Rollover
                          </MuiButton>
                        </MenuItem>
                      ) : null}
                      {permissions &&
                      permissions[permissionNames.hos.logs.reassign] ? (
                        <MenuItem>
                          <MuiButton
                            color="primary"
                            variant="text"
                            onClick={(e) => {
                              setDialogOpen(true);
                              setMasterMenuAnchorEl(null);
                              e.stopPropagation();
                            }}
                            startIcon={
                              <Icon
                                path={mdiAccountSwitchOutline}
                                size="16px"
                              />
                            }
                          >
                            {translate(
                              "resources.hos-driver-log-view.labels.reassignEvents"
                            )}
                          </MuiButton>
                        </MenuItem>
                      ) : null}
                      {permissions &&
                      permissions[permissionNames.hos.logs.reassign] ? (
                        <MenuItem>
                          <MuiButton
                            color="primary"
                            variant="text"
                            onClick={(e) => {
                              setFixEventsDialogOpen(true);
                              setMasterMenuAnchorEl(null);
                              e.stopPropagation();
                            }}
                            startIcon={<Icon path={mdiAutoFix} size="16px" />}
                          >
                            Fix events
                          </MuiButton>
                        </MenuItem>
                      ) : null}
                      {permissions &&
                      (permissions[permissionNames.hos.logs.rollover] ||
                        permissions[permissionNames.hos.logs.reassign]) ? (
                        <MenuItem>
                          <ViewDriverPasswordAction
                            showUsername
                            record={{
                              id: record.driverId!,
                              driverName: record.driverFullName,
                            }}
                          >
                            <MuiButton
                              color="primary"
                              variant="text"
                              onClick={(e) => {
                                setMasterMenuAnchorEl(null);
                              }}
                              startIcon={<VisibilityOutlined />}
                            >
                              View driver password
                            </MuiButton>
                          </ViewDriverPasswordAction>
                        </MenuItem>
                      ) : null}
                      {permissions &&
                      (permissions[permissionNames.hos.logs.rollover] ||
                        permissions[permissionNames.hos.logs.reassign]) ? (
                        <MenuItem>
                          <MuiButton
                            color="primary"
                            variant="text"
                            onClick={() => {
                              setMasterMenuAnchorEl(null);
                              setLogoutConfirmOpen(true);
                            }}
                            startIcon={<ExitToApp />}
                          >
                            Log the driver out
                          </MuiButton>
                        </MenuItem>
                      ) : null}
                    </Menu>
                  </>
                }
                medium={
                  <>
                    {permissions &&
                    (permissions[permissionNames.hos.logs.rollover] ||
                      permissions[permissionNames.hos.logs.reassign]) ? (
                      <Button
                        className="master-action"
                        label="Shift events"
                        onClick={(e) => {
                          setShiftDialogOpen(true);
                          e.stopPropagation();
                        }}
                      >
                        <SwapHoriz />
                      </Button>
                    ) : null}
                    {permissions &&
                    permissions[permissionNames.hos.logs.rollover] ? (
                      <Button
                        className="master-action"
                        label="Rollover"
                        onClick={(e) => {
                          setRolloverConfirmOpen(true);
                          e.stopPropagation();
                        }}
                      >
                        <FindReplace />
                      </Button>
                    ) : null}
                    {permissions &&
                    permissions[permissionNames.hos.logs.reassign] ? (
                      <Button
                        className="master-action"
                        label="resources.hos-driver-log-view.labels.reassignEvents"
                        onClick={(e) => {
                          setDialogOpen(true);
                          e.stopPropagation();
                        }}
                      >
                        <Icon path={mdiAccountSwitchOutline} size="16px" />
                      </Button>
                    ) : null}
                    {/* {permissions &&
                    permissions[permissionNames.hos.logs.reassign] ? (
                      <Button
                        className="master-action"
                        label="Fix events"
                        onClick={(e) => {
                          setFixEventsDialogOpen(true);
                          e.stopPropagation();
                        }}>
                        <Icon path={mdiAutoFix} size="16px" />
                      </Button>
                    ) : null} */}
                    {permissions &&
                    (permissions[permissionNames.hos.logs.rollover] ||
                      permissions[permissionNames.hos.logs.reassign]) ? (
                      <ViewDriverPasswordAction
                        showUsername
                        record={{
                          id: record.driverId!,
                          driverName: record.driverFullName,
                        }}
                      >
                        <Button
                          className="master-action"
                          label="View driver password"
                        >
                          <VisibilityOutlined />
                        </Button>
                      </ViewDriverPasswordAction>
                    ) : null}
                    {permissions &&
                    (permissions[permissionNames.hos.logs.rollover] ||
                      permissions[permissionNames.hos.logs.reassign]) ? (
                      <Button
                        className="master-action"
                        label="Log the driver out"
                        onClick={() => {
                          setLogoutConfirmOpen(true);
                        }}
                      >
                        <ExitToApp />
                      </Button>
                    ) : null}
                  </>
                }
              />
            </div>
          </>
        ) : null}
        {record.driverFmcsaInspection ? (
          permissions &&
          permissions[permissionNames.hos.fmcsaDataTransfer.main] ? (
            <Button
              className={classes.warning}
              label="FMCSA inspected"
              onClick={() =>
                history.push(
                  `/${resourceNames.HOS_FMCSA_DATA_TRANSFER}/${record.driverFmcsaInspection?.id}/show`
                )
              }
            >
              <ErrorOutline />
            </Button>
          ) : (
            <Box
              className={classNames(classes.warningBox, classes.warning)}
              display="flex"
              alignItems="center"
            >
              <ErrorOutline />
              <Typography variant="body2">FMCSA inspected</Typography>
            </Box>
          )
        ) : null}
        {logDate && driverId ? (
          <Button
            disabled={loading}
            label="Cycle recap"
            onClick={() => setRecapOpen(true)}
          >
            <DateRange />
          </Button>
        ) : null}
        <Button
          disabled={loading}
          label="resources.common.labels.download"
          onClick={() => {
            if (!axios) {
              return;
            }

            if (!driverId) {
              return;
            }

            if (!logDate) {
              return;
            }

            if (downloading) {
              return;
            }

            setDownloading(true);
            // const graphHtml = ReactDOMServer.renderToStaticMarkup(
            //   <StaticRenderer
            //     theme={createTheme()}
            //     history={history}
            //     dataProvider={dataProvider}>
            //     <HosGraphView
            //       record={record}
            //       onHover={() => {}}
            //       onEventClick={() => {}}
            //       history={props.history}
            //     />
            //   </StaticRenderer>
            // );
            ReportApiApiFactory(undefined, axios?.defaults.baseURL, axios)
              .reportDailylogreportPost({
                reportName: "daily-log",
                driverId,
                logDate: logDate.toISOString(),
              })
              .then((response) => {
                notify("resources.reports.labels.reportRequested", "success");
                hub?.connection?.invoke(
                  "SubscribeToReportUpdates",
                  response.data
                );
              })
              .catch((e) => {
                console.error(e);
                notify("resources.reports.labels.error", "error");
              })
              .finally(() => {
                setDownloading(false);
              });
          }}
        >
          {!!downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
        </Button>
        <ListButton
          basePath={`/${resourceNames.HOS_DRIVER_LOGS}/${record.driverId}`}
          label="resources.common.labels.backToList"
        />
      </TopToolbar>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {translate("resources.hos-driver-log-view.labels.reassignEvents")}
        </DialogTitle>
        <Divider />
        <Card>
          <ReassignEventsForm
            record={record}
            onSuccess={handleSuccess}
            onFailed={handleFailed}
          />
        </Card>
      </Dialog>
      <Dialog
        open={recapOpen}
        onClose={() => setRecapOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Current cycle recap</DialogTitle>
        <Divider />
        <Card>
          <CycleRecapView
            logDate={logDate!}
            driverId={driverId!}
            open={recapOpen}
            onClose={() => setRecapOpen(false)}
            isMaster={
              permissions &&
              (permissions[permissionNames.hos.logs.rollover] ||
                permissions[permissionNames.hos.logs.reassign])
            }
          />
        </Card>
      </Dialog>
      <Dialog
        open={rolloverConfirmOpen}
        onClose={() => setRolloverConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm rollover</DialogTitle>
        <Divider />
        <Card>
          <RolloverForm
            record={record}
            onSuccess={handleRolloverSuccess}
            onFailed={handleRolloverFailed}
          />
        </Card>
      </Dialog>
      <Dialog
        open={shiftDialogOpen}
        onClose={() => setShiftDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Shift events</DialogTitle>
        <Divider />
        <Card>
          <ShiftEventsForm
            record={record}
            onSuccess={handleShiftSuccess}
            onFailed={handleShiftFailed}
          />
        </Card>
      </Dialog>
      <Confirm
        isOpen={logoutConfirmOpen}
        title="Confirm driver logout"
        content="Are you sure you want to log this driver out?"
        onConfirm={handleLogout}
        onClose={() => setLogoutConfirmOpen(false)}
      />
      <Dialog
        open={fixEventsDialogOpen}
        onClose={() => setFixEventsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fix events</DialogTitle>
        <Divider />
        <Card>
          <FixEventsForm
            record={record}
            onSuccess={handleFixEventsSuccess}
            onFailed={handleFixEventsFailed}
          />
        </Card>
      </Dialog>
    </>
  );
};

const Breadcrumb = ({ driver, logDate }: BreadcrumbProps) => {
  const translate = useTranslate();
  const params = useParams<CustomResourceRouteParams>();
  return (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to={`/${resourceNames.HOS_LOGS}`}>
        <Typography variant="h6">
          {translate("resources.hos-logs.name")}
        </Typography>
      </Link>
      <DriverNameBreadcrumb
        driver={driver}
        getBasePath={(x) =>
          `/${resourceNames.HOS_DRIVER_LOG_VIEW}/${x.id}/${params["id"]}`
        }
        getLink={(x) => `/${resourceNames.HOS_DRIVER_LOGS}/${x.id}`}
      />
      <Typography variant="h6">{format(logDate!, "MMM do")}</Typography>
    </Breadcrumbs>
  );
};

export const DriverLogView = (
  props: DriverLogViewProps &
    ReactAdminComponentProps &
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
) => {
  const classes = useStyles(props);
  const { driverId, logDate } = useDriverLogViewParameters(props);
  const { driverLog: record, loading, refetch } = useDriverLog(
    driverId,
    logDate
  );
  const [hoveredEventId, setHoveredEventId] = useState<Identifier>();
  const permissions = useActionPermissions();
  const gridRef = useRef<IDriverLogGridView>(null);

  const handleEventClick = useCallback((id: Identifier) => {
    if (!gridRef.current) {
      return;
    }

    gridRef.current.selectEvent(id);
  }, []);

  const handleDrawerClose = useCallback(() => {
    props.history?.push(
      `/${
        resourceNames.HOS_DRIVER_LOG_VIEW
      }/${driverId}/${logDate?.toISOString()}`
    );
    // refresh();
  }, [driverId, logDate, props.history]);

  const contextValue = useMemo(() => {
    return {
      hoveredEventId,
    };
  }, [hoveredEventId]);

  const onHover = useCallback((id?: Identifier) => setHoveredEventId(id), []);
  const refresh = useRefresh();
  const selectedDriver = useMemo(
    () => ({
      id: record?.driverId,
      fullName: record?.driverFullName,
      driverId: record?.driverAssignedId,
    }),
    [record?.driverFullName, record?.driverId, record?.driverAssignedId]
  );

  if (!record) {
    return null;
  }

  return (
    <div
      className={classnames(
        "show-page",
        classes.root,
        props.className,
        "content-container"
      )}
      {...sanitizeRestProps(props)}
    >
      <TitleForRecord
        title={<Breadcrumb driver={selectedDriver} logDate={logDate} />}
        record={record}
      />
      <ViewActions
        record={record}
        driverId={driverId}
        logDate={logDate}
        loading={loading}
        {...props}
      />
      {loading ? (
        <Grid container>
          <Grid
            item
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className="loading-wrapper-extend"
          >
            <Loading />
          </Grid>
        </Grid>
      ) : (
        <>
          <Paper id="driver-log-view-container" className={classes.card}>
            <DriverLogHeader record={record} onHosDataRefresh={refetch} />
            <Divider />
            <EventHoverContext.Provider value={contextValue}>
              <HosGraphView
                record={record}
                history={props.history}
                onHover={onHover}
                onEventClick={handleEventClick}
                isMaster={
                  permissions && permissions[permissionNames.hos.logs.rollover]
                }
              />
              <HosDriverLogGridView
                record={record}
                onHover={onHover}
                {...props}
                ref={gridRef}
              />
            </EventHoverContext.Provider>
          </Paper>
          <Paper className={classes.card}>
            <DriverLogViewFooter className={classes.card} record={record} />
            <Divider />
            <DriverSignature record={record} />
          </Paper>
          <Paper className={classes.card}>
            <DriverLogExceptionsView record={record} />
          </Paper>
          <Paper className={classes.card}>
            <DriverLogInspectionReportsView record={record} />
          </Paper>
          <DriverLogViewChildRoutes
            onClose={handleDrawerClose}
            onSuccess={() => {
              handleDrawerClose();
              refresh();
            }}
            record={record}
          />
        </>
      )}
    </div>
  );
};

const sanitizeRestProps = ({
  basePath = null,
  defaultTitle = null,
  hasCreate = null,
  hasEdit = null,
  hasList = null,
  hasShow = null,
  history = null,
  id = null,
  loaded = null,
  loading = null,
  location = null,
  match = null,
  options = null,
  permissions = null,
  ...rest
}: any) => rest;
